import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

/**
 * 7 dots representing the last 7 Dhaka days (oldest → newest).
 *   - intensity per day = count / daily_target
 *   - the rightmost dot = "today" (has a white ring)
 *   - filled dots use `bg-accent/*` so they follow the active theme
 *
 * Props:
 *   - days   [{date, count}, …] length 7, oldest → newest
 *   - target daily target (int > 0)
 *   - nudge  when true, adds a whisper-subtle muted-red pulse every ~22s
 *            around the whole row (used by the streak-saver after 8 PM
 *            Dhaka on links that haven't met target yet).
 */
export default function HeatmapDots({ days = [], target = 1, nudge = false }) {
  const today = days[days.length - 1];
  // Feb-2026 random daily targets: each day object may carry its own
  // effective target (`d.target`). Fall back to the row-level `target`
  // prop for legacy servers that don't emit it. This keeps historical
  // days honest — a day that had target=2 stays coloured against 2,
  // not against today's fresh roll.
  const effectiveDayTarget = (d) =>
    Number.isFinite(d?.target) && d.target > 0 ? d.target : target;
  const todayTarget = today ? effectiveDayTarget(today) : target;
  const isTargetMet = !!today && todayTarget > 0 && today.count >= todayTarget;

  // Fire the celebratory wave the instant target_met flips false → true.
  const prevMetRef = useRef(isTargetMet);
  const [wave, setWave] = useState(false);
  useEffect(() => {
    if (isTargetMet && !prevMetRef.current) {
      setWave(true);
      const t = window.setTimeout(() => setWave(false), 780);
      prevMetRef.current = isTargetMet;
      return () => window.clearTimeout(t);
    }
    prevMetRef.current = isTargetMet;
    return undefined;
  }, [isTargetMet]);

  return (
    <div
      className={
        "inline-flex items-center gap-1 px-1 " +
        (nudge && !isTargetMet ? "nudge-pulse" : "")
      }
      data-testid="heatmap-dots"
      data-nudge={nudge ? "true" : "false"}
    >
      {days.map((d, idx) => {
        const isToday = idx === days.length - 1;
        const dayTarget = effectiveDayTarget(d);
        const ratio = dayTarget > 0 ? d.count / dayTarget : 0;
        const level = pickLevel(ratio);
        // Only today's dot re-keys on count change so it pops on ±1.
        const key = isToday ? `today-${d.count}` : d.date;

        return (
          <motion.span
            key={key}
            title={`${d.date} — ${d.count}/${dayTarget}`}
            data-testid="heatmap-dot"
            data-date={d.date}
            data-count={d.count}
            data-level={level.label}
            initial={
              isToday
                ? { scale: 0.35, opacity: 0.4 }
                : { scale: 0.6, opacity: 0 }
            }
            animate={
              wave
                ? { scale: [1, 1.45, 1], opacity: 1 }
                : { scale: 1, opacity: 1 }
            }
            transition={
              wave
                ? {
                    duration: 0.55,
                    ease: [0.16, 1, 0.3, 1],
                    delay: idx * 0.055,
                  }
                : {
                    type: "spring",
                    stiffness: 340,
                    damping: 18,
                    delay: idx * 0.04,
                  }
            }
            className={
              "h-2.5 w-2.5 rounded-sm " +
              level.className +
              (isToday ? " ring-1 ring-white/40" : "")
            }
            style={{
              ...(level.style || {}),
              transition:
                "background-color 500ms ease, box-shadow 500ms ease",
            }}
          />
        );
      })}
    </div>
  );
}

function pickLevel(ratio) {
  if (ratio <= 0)
    return { label: "0", className: "bg-white/10", style: undefined };
  if (ratio < 0.5)
    return {
      label: "25",
      className: "bg-accent/40",
      style: { boxShadow: "0 0 4px rgb(var(--accent) / 0.35)" },
    };
  if (ratio < 1)
    return {
      label: "60",
      className: "bg-accent/70",
      style: { boxShadow: "0 0 6px rgb(var(--accent) / 0.5)" },
    };
  return {
    label: "100",
    className: "bg-accent",
    style: { boxShadow: "0 0 10px rgb(var(--accent) / 0.75)" },
  };
}
